/**
 * Pure row logic for the LAE Productivity Schools table and the School modal (P6). No React.
 *
 * Ports from the LAE app (`lae-app/src/features/productivity/`):
 *   - `shared/schoolColumns.jsx` schoolRowsOf: groups[] (school x funnel) to table rows, the subtotal
 *     flattened onto the row plus the stacked assignment lines;
 *   - `StackedCell.jsx` linesOf, singleValue and the subtotal date helpers earliestStart / latestEnd,
 *     plus the people count inside SpecialistsTotal (here `specialistTotals`);
 *   - `SchoolsTable.jsx` sort: by the subtotal value, nulls last in either direction, ties by school
 *     name then funnel label;
 *   - `SchoolsView.jsx` nextSort and groupSpecialists;
 *   - `SchoolOutcomesBlock.jsx` consolidate (here `consolidateOutcomes`);
 *   - `shared/useSchoolModalHero.js` row pick (here `schoolGroupsOf` + `pickSchoolRow`).
 *
 * Deliberate differences:
 *   - Rows carry `emerge_company` through unchanged (owner, 2026-10-29). Undefined stays undefined
 *     (an older API), null stays null (no mapped company).
 *   - groupSpecialists keys people by `id ?? name` (LAE used `id` only) and ORs `assigned` as well as
 *     `worked`, so it also serves the specialists subtotal.
 *   - consolidateOutcomes follows spec 7.2: one group returns its subtotal even when totals are given
 *     (LAE preferred totals first; for one group they are the same figures).
 *   - The row pick is split in two: `schoolGroupsOf` (the modal's groups) and `pickSchoolRow` (the row).
 *   - Zebra tint is not ported (spec 5.2).
 */
import type {
  LaeProdAssignmentLine,
  LaeProdGroupRow,
  LaeProdMetrics,
  LaeProdSchoolRow,
  LaeProdSeriesPoint,
  LaeProdSpecialist,
  LaeProductivitySchoolResponse,
  LaeProductivitySchoolsResponse,
} from '@/lib/types';

/** The mapped SEED EMERGE company on a group, a school row or the modal's school. */
export interface LaeEmergeCompany {
  id: number;
  name: string;
}

interface WithEmergeCompany {
  /** undefined = an older API; null = no mapped company. */
  emerge_company?: LaeEmergeCompany | null;
}

/** A `groups[]` entry as the backend sends it, with the company link. */
export type LaeSchoolGroup = LaeProdGroupRow & WithEmergeCompany;
/** A `schools[]` entry as the backend sends it, with the company link. */
export type LaeSchoolListRow = LaeProdSchoolRow & WithEmergeCompany;

/** One table row: one school x funnel, the subtotal flattened on, plus the stacked lines. */
export interface LaeSchoolRow extends LaeProdMetrics, WithEmergeCompany {
  /** `<school_id>|<funnel>`. */
  key: string;
  school_id: string;
  school_name: string | null;
  /** `''` when the row has no funnel (older response under All funnels). */
  funnel: string;
  funnel_label: string;
  /** Lines drawn in the cell: every line when split, `[]` when consolidated or without a breakdown. */
  assignments: LaeProdAssignmentLine[];
  /** Every line, kept only when consolidated (counts, specialist totals, export). */
  __allLines?: LaeProdAssignmentLine[];
  /** Earliest line start / latest line end; null when none. */
  start_date: string | null;
  end_date: string | null;
  specialists_assigned?: number;
  specialists_worked?: number;
  specialists_total?: number;
  /** Present on rows built from `schools[]` (no breakdown). */
  counsellors_active?: number;
  series?: LaeProdSeriesPoint[];
}

export type SortDir = 'asc' | 'desc';
export interface LaeSort {
  key: string;
  dir: SortDir;
}

type LineLike = { start_date?: string | null; end_date?: string | null };
type LinesHolder = { assignments?: unknown } | null | undefined;

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

/** The row's drawn lines (`assignments`), or `[]`. */
export function linesOf(row: LinesHolder): LaeProdAssignmentLine[] {
  const a = row?.assignments;
  return Array.isArray(a) ? (a as LaeProdAssignmentLine[]) : [];
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

function datesOf(source: LinesHolder | readonly LineLike[], key: 'start_date' | 'end_date'): string[] {
  const lines: readonly LineLike[] = Array.isArray(source) ? source : linesOf(source as LinesHolder);
  const out: string[] = [];
  for (const line of lines) {
    const d = line?.[key];
    if (typeof d === 'string' && ISO_DAY.test(d)) out.push(d.slice(0, 10));
  }
  return out.sort();
}

/** Earliest `start_date` across a row's lines (or a list of lines), or null. */
export function earliestStart(source: LinesHolder | readonly LineLike[]): string | null {
  return datesOf(source, 'start_date')[0] ?? null;
}

/** Latest `end_date` across a row's lines (or a list of lines), or null. */
export function latestEnd(source: LinesHolder | readonly LineLike[]): string | null {
  const d = datesOf(source, 'end_date');
  return d[d.length - 1] ?? null;
}

/** Fields that identify an assignment line; everything else on a one-line row comes from the subtotal. */
const LINE_IDENTITY = [
  'assignment_id',
  'name',
  'assignment_type',
  'cycle',
  'status',
  'start_date',
  'end_date',
  'specialists',
] as const;

export type LaeSingleValue<R> = R &
  Partial<Pick<LaeProdAssignmentLine, (typeof LINE_IDENTITY)[number]>> & { __single: true; __total: false };

/**
 * The value a one-assignment row draws: the subtotal (the row) with the line's identity laid over it,
 * marked `__single` so renderers treat it as the row's total.
 */
export function singleValue<R extends object>(row: R, line: LaeProdAssignmentLine | null | undefined): LaeSingleValue<R> {
  const v: Record<string, unknown> = { ...row, __single: true, __total: false };
  if (line) {
    for (const f of LINE_IDENTITY) if (f in line) v[f] = line[f];
  }
  return v as LaeSingleValue<R>;
}

/**
 * groups[] to table rows. With `groups` (even empty) one row per group; without it (an older
 * response) one subtotal-only row per `schools[]` entry keyed by the page funnel.
 */
export function schoolRowsOf(
  groups: readonly LaeSchoolGroup[] | null | undefined,
  schools: readonly LaeSchoolListRow[] | null | undefined,
  funnel: string | null | undefined,
  split = true,
): LaeSchoolRow[] {
  if (Array.isArray(groups)) {
    return groups.map((g): LaeSchoolRow => {
      const all = Array.isArray(g.assignments) ? g.assignments : [];
      const row: LaeSchoolRow = {
        ...g.subtotal,
        key: g.key ?? `${g.school_id}|${g.funnel ?? ''}`,
        school_id: g.school_id,
        school_name: g.school_name,
        funnel: g.funnel ?? '',
        funnel_label: g.funnel_label ?? '',
        assignments: split ? all : [],
        start_date: earliestStart(all),
        end_date: latestEnd(all),
      };
      if (!split) row.__allLines = all;
      if ('emerge_company' in g) row.emerge_company = g.emerge_company;
      return row;
    });
  }
  return (schools ?? []).map(
    (s): LaeSchoolRow => ({
      ...s,
      key: `${s.school_id}|${funnel ?? ''}`,
      funnel: funnel ?? '',
      funnel_label: '',
      assignments: [],
      start_date: null,
      end_date: null,
    }),
  );
}

const collator = new Intl.Collator('en-GB', { sensitivity: 'base' });

/** Compares two non-blank values: numbers numerically, everything else as en-GB text. */
function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

type SortableRow = { school_name?: string | null; funnel?: string | null; funnel_label?: string | null };

/**
 * Sorted copy of `rows` by `sortValue` (the subtotal value). Blank values (null, undefined, '') sort
 * last in either direction. Ties break by school name, then funnel label. Lines are not touched.
 */
export function sortSchoolRows<R extends SortableRow>(
  rows: readonly R[],
  sortValue: (row: R) => string | number | null | undefined,
  dir: SortDir,
): R[] {
  const sign = dir === 'asc' ? 1 : -1;
  const nameOf = (r: R) => String(r.school_name ?? '');
  const funnelOf = (r: R) => String(r.funnel_label || r.funnel || '');
  return [...rows].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    const aBlank = isBlank(va);
    const bBlank = isBlank(vb);
    let c = 0;
    if (aBlank && !bBlank) c = 1;
    else if (bBlank && !aBlank) c = -1;
    else if (!aBlank && !bBlank) c = sign * compareValues(va as string | number, vb as string | number);
    return c || collator.compare(nameOf(a), nameOf(b)) || collator.compare(funnelOf(a), funnelOf(b));
  });
}

const NAME_KEYS = new Set(['name', 'school', 'school_name']);

/** Header-click sort: the same key flips; a new key starts desc, except name keys, which start asc. */
export function nextSort(current: LaeSort | null | undefined, key: string): LaeSort {
  if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: NAME_KEYS.has(key) ? 'asc' : 'desc' };
}

/** One person across a group's lines. */
export interface LaeSpecialistPerson {
  id: string;
  name: string;
  /** Worked on any line in the window. */
  worked: boolean;
  /** On any line's team roster; undefined when no line said. */
  assigned?: boolean;
}

/**
 * Every specialist across the lines, once each (by `id ?? name`); worked if they worked on any line.
 * Worked first, then by name.
 */
export function groupSpecialists(
  assignments: readonly { specialists?: readonly LaeProdSpecialist[] | null }[] | null | undefined,
): LaeSpecialistPerson[] {
  const people = new Map<string, LaeSpecialistPerson>();
  for (const a of assignments ?? []) {
    const list = Array.isArray(a?.specialists) ? a.specialists : [];
    for (const p of list) {
      const rawId = p?.id ?? p?.name;
      if (rawId === null || rawId === undefined || rawId === '') continue;
      const id = String(rawId);
      const seen = people.get(id);
      const assigned =
        p.assigned === undefined && seen?.assigned === undefined ? undefined : Boolean(seen?.assigned || p.assigned);
      const person: LaeSpecialistPerson = {
        id,
        name: seen?.name || p.name || `#${id}`,
        worked: Boolean(seen?.worked || p.worked),
      };
      if (assigned !== undefined) person.assigned = assigned;
      people.set(id, person);
    }
  }
  return [...people.values()].sort(
    (x, y) => Number(y.worked) - Number(x.worked) || collator.compare(x.name, y.name),
  );
}

/**
 * The specialists subtotal: worked over everyone listed. Uses the server's `specialists_worked` /
 * `specialists_total` when they are numbers, otherwise counts distinct people across
 * `__allLines ?? assignments`. `total` is raised to `worked` when lower. Never `specialists_assigned`:
 * someone off the roster can work.
 */
export function specialistTotals(row: {
  assignments?: unknown;
  __allLines?: readonly LaeProdAssignmentLine[];
  specialists_worked?: number | null;
  specialists_total?: number | null;
}): { worked: number; total: number; people: LaeSpecialistPerson[] } {
  const people = groupSpecialists(row.__allLines ?? linesOf(row));
  const worked = isFiniteNumber(row.specialists_worked)
    ? row.specialists_worked
    : people.filter((p) => p.worked).length;
  let total = isFiniteNumber(row.specialists_total) ? row.specialists_total : people.length;
  if (total < worked) total = worked;
  return { worked, total, people };
}

/** Average keys rebuilt from their summed seconds / calls (the backend's rule). */
const AVG_OF = {
  avg_ccm_talk_seconds: ['ccm_talk_seconds', 'ccm_calls'],
  avg_adv_talk_seconds: ['adv_talk_seconds', 'adv_calls'],
  avg_neg_talk_seconds: ['neg_talk_seconds', 'neg_calls'],
} as const satisfies Record<string, readonly [keyof LaeProdMetrics, keyof LaeProdMetrics]>;

/** The outcome keys except the averages, plus the seconds / calls the averages are rebuilt from. */
const SUM_KEYS = [
  'counselling_completed',
  'ccm_talk_seconds',
  'advanced',
  'adv_talk_seconds',
  'regressed',
  'neg_talk_seconds',
  'applications',
  'ccm_calls',
  'adv_calls',
  'neg_calls',
] as const satisfies readonly (keyof LaeProdMetrics)[];

/** The consolidated outcomes row. Keys a sum could not see are null, never 0. */
export type LaeOutcomesRow = { [K in keyof LaeProdMetrics]?: LaeProdMetrics[K] | null };

/**
 * Consolidated outcomes row for the School modal: one group gives its subtotal (the exact table row);
 * several give the caller's person-deduplicated `totals` when present, otherwise a plain sum of the
 * group subtotals over SUM_KEYS with the averages recomputed (null when calls = 0). No groups and no
 * totals: an empty row.
 */
export function consolidateOutcomes(
  groups: readonly LaeSchoolGroup[] | null | undefined,
  totals: LaeOutcomesRow | null | undefined,
): LaeOutcomesRow {
  const list = Array.isArray(groups) ? groups : [];
  const first = list[0];
  if (list.length === 1 && first) return first.subtotal;
  if (totals) return totals;
  if (list.length === 0) return {};
  const out: Record<string, number | null> = {};
  for (const key of SUM_KEYS) {
    let seen = false;
    let sum = 0;
    for (const g of list) {
      const v: unknown = g.subtotal?.[key];
      if (!isFiniteNumber(v)) continue;
      seen = true;
      sum += v;
    }
    out[key] = seen ? sum : null;
  }
  for (const [avg, [secs, calls]] of Object.entries(AVG_OF)) {
    const s = out[secs];
    const c = out[calls];
    out[avg] = isFiniteNumber(s) && isFiniteNumber(c) && c > 0 ? Math.round(s / c) : null;
  }
  return out as LaeOutcomesRow;
}

type SchoolsListData = Pick<LaeProductivitySchoolsResponse, 'schools'> & {
  groups?: readonly LaeSchoolGroup[];
  schools: readonly LaeSchoolListRow[];
};
type SchoolData = Pick<LaeProductivitySchoolResponse, 'totals'>;

/**
 * The page list's groups for one school, narrowed to `funnel` when it is set (the modal's
 * `schoolFunnel`). `[]` when the list has no breakdown or is not loaded.
 */
export function schoolGroupsOf(
  listData: SchoolsListData | null | undefined,
  schoolId: string | null | undefined,
  funnel: string | null | undefined,
): LaeSchoolGroup[] {
  const all = Array.isArray(listData?.groups) ? listData.groups : [];
  const sid = String(schoolId ?? '');
  const pick = funnel ?? '';
  return all.filter((g) => String(g.school_id) === sid && (!pick || (g.funnel ?? '') === pick));
}

/** The row the School modal hero draws: list groups (one or several), else the list's school row. */
export type LaeSchoolHeroRow = LaeProdMetrics &
  WithEmergeCompany & {
    series?: LaeProdSeriesPoint[];
    specialists_worked?: number;
    specialists_total?: number;
  };

/**
 * The School modal's hero row, so the modal equals the table row for the same filters:
 * one matching group gives its subtotal; several give the school endpoint's person-deduplicated
 * `totals`; none (no breakdown) give the list's school row, else the endpoint totals. Null while
 * nothing is loaded.
 */
export function pickSchoolRow(
  listData: SchoolsListData | null | undefined,
  schoolId: string | null | undefined,
  funnel: string | null | undefined,
  schoolData: SchoolData | null | undefined,
): LaeSchoolHeroRow | null {
  const groups = schoolGroupsOf(listData, schoolId, funnel);
  const first = groups[0];
  if (groups.length === 1 && first) return first.subtotal;
  if (groups.length > 1) return schoolData?.totals ?? null;
  const sid = String(schoolId ?? '');
  const s = Array.isArray(listData?.schools) ? listData.schools.find((x) => String(x.school_id) === sid) : undefined;
  return s ?? schoolData?.totals ?? null;
}
